// Rule engine for CPQ parsed_*_rule expressions.
//
// Parsed expressions look like:
//   true
//   false
//   (__["HOUSING/HOUSING"]=="Box-Type")
//   !((__["HOUSING/HOUSING"]=="Hinge Type"))
//   (__["BURNER_QTY/MAIN_BURNER_0"])
//   !((__["FIRETUBE_QUANTITY/FIRETUBE_QUANTITY_2"])||(__["FIRETUBE_QUANTITY/FIRETUBE_QUANTITY_3"]))
//   _("MATERIAL_VIEW/*")
//
// __  is an accessor: __["Q_CODE/A_CODE"] returns the current answer value
//     (true when a picklist option is selected; the typed string for free-text;
//     null/undefined when not set).
// _   is a wildcard helper: _("FOO/*") is truthy if any FOO/* key is truthy.
//
// Expressions are extracted from a trusted NetSuite CPQ export. We compile each
// unique expression with new Function() once and cache it.

import type { AnswerState } from "../types";

const ALWAYS_HIDE_SENTINEL = "*" + "/" + "*";

type CompiledRule = (state: AnswerState) => unknown;

const compileCache = new Map<string, CompiledRule>();

function compile(expr: string): CompiledRule {
  const cached = compileCache.get(expr);
  if (cached) return cached;

  let body: (a: unknown, b: unknown) => unknown;
  try {
    body = new Function(
      "__",
      "_",
      `"use strict"; return (${expr});`
    ) as (a: unknown, b: unknown) => unknown;
  } catch (err) {
    if (import.meta.env?.DEV) {
      console.warn("[rule] failed to compile:", expr, err);
    }
    const noop: CompiledRule = () => false;
    compileCache.set(expr, noop);
    return noop;
  }

  const fn: CompiledRule = (state) => {
    const proxy = new Proxy(
      {},
      {
        get: (_target, key: string | symbol) => {
          if (typeof key !== "string") return undefined;
          return state[key];
        },
      }
    );
    const wildcard = (pattern: string): boolean => {
      if (pattern.endsWith("/*")) {
        const prefix = pattern.slice(0, -1);
        for (const k of Object.keys(state)) {
          if (k.startsWith(prefix) && state[k]) return true;
        }
        return false;
      }
      return Boolean(state[pattern]);
    };
    try {
      return body(proxy, wildcard);
    } catch (err) {
      if (import.meta.env?.DEV) {
        console.warn("[rule] eval failed:", expr, err);
      }
      return false;
    }
  };
  compileCache.set(expr, fn);
  return fn;
}

export function evalRule(parsedRule: string, state: AnswerState): boolean {
  const expr = parsedRule.trim();
  if (!expr) return false;
  if (expr === "true") return true;
  if (expr === "false") return false;
  return Boolean(compile(expr)(state));
}

export type HideRuleInput = {
  hideRule?: string;
  parsedHideRule?: string;
  hideFlag?: boolean;
};

export function isHidden(
  rule: HideRuleInput,
  state: AnswerState
): boolean {
  if (rule.hideFlag === true) return true;
  if ((rule.hideRule ?? "").trim() === ALWAYS_HIDE_SENTINEL) return true;
  return evalRule(rule.parsedHideRule ?? "", state);
}
